import React from 'react'
import { Table, TableBody, TableHead, TableHeader, TableRow, TableCell } from './ui-primitives/table'
import { MinusIcon, PlusIcon } from '@radix-ui/react-icons'
import { useDebounce } from '../hooks/use-debounce'
import { Input } from './ui-primitives/input'
import { Button } from './ui-primitives/button'
import { produce } from 'immer'

interface FixedLengthArray<T extends any, L extends number> extends Array<T> {
  0: T;
	length: L;
}

type Props<
  ColumnName extends string,
  Row extends FixedLengthArray<string, Length>,
  Length extends number,
> = {
  columnLabels: FixedLengthArray<ColumnName, Length>
  disabledColumnIndexes?: number[]
  initialRows: Row[]
  onChange: (rows: Row[]) => void | Promise<void>
  debounceTime?: number
  cannotDeleteRows?: boolean
  deletesEmptyValueRows?: boolean
} & ( {}
    | { createNewRow: () => Row }
    | { createNewRow: () => Row, minimumNumberOfRows?: number }
    )

export function StringTableInput<
  ColumnName extends string,
  Row extends FixedLengthArray<string, Length>,
  Length extends number,
>(props: Props<ColumnName, Row, Length>): React.ReactNode {

  const [rows, setRows] = React.useState(() => {
    if (!('minimumNumberOfRows' in props)) return props.initialRows
    if (props.minimumNumberOfRows == null) return props.initialRows

    const draft = [...props.initialRows]
    while (draft.length < props.minimumNumberOfRows) draft.push(props.createNewRow())
    return draft
  })

  useDebounce(() => {
    if (!('deletesEmptyValueRows' in props && props.deletesEmptyValueRows)) {
      return props.onChange(rows)
    }

    const fullyFilledRows = rows.filter(row => Object.values(row).every(x => x))
    props.onChange(fullyFilledRows)
  }, props.debounceTime ?? 200, [rows])

  const canDeleteRows = !('cannotDeleteRows' in props && props.cannotDeleteRows)

  return (
    <div>
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-inherit">
            {props.columnLabels.map(label => (
              <TableHead>{label}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, iRow) => (
            <TableRow key={iRow} className="hover:bg-inherit border-none">
              {row.map((value, iCol) => (
                <TableCell key={iCol} className="p-0 pr-1">
                  <Input type="text"
                    value={value}
                    onChange={e => setRows(produce(draft => {
                      draft[iRow][iCol] = e.target.value
                    }))}
                    disabled={props.disabledColumnIndexes?.includes(iCol)}
                  />
                </TableCell>
              ))}
              {canDeleteRows && (
                <TableCell>
                  <Button variant="ghost" className="rounded-full"
                    onClick={() => setRows(rows => rows.filter((_, i) => i !== iRow))}
                  ><MinusIcon /></Button>
                </TableCell>
              )}
            </TableRow>))
          }</TableBody>
      </Table>
      {'createNewRow' in props && (
        <Button variant="ghost"
          onClick={() => setRows(rows => [...rows, props.createNewRow()])}
        ><PlusIcon /></Button>
      )}
    </div>
  )

}
